import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import BusinessUnit from "../../interfaces/BusinessUnit";
import {
  updateBusinessUnitInfo,
  createNewBusinessUnit,
  getAllBusinessUnits,
  getBusinessUnitById,
  bulkImportUpload,
  updateBusinessUnitListInfo,
} from "../../utils/api/BusinessUnitApi";
import { RootState } from "../store/store";
import { FAILED, IDLE, LOADING, NEEDS_REFRESH, SUCCEEDED } from "./constants";
import BuiltBusinessUnit from "../../interfaces/BuiltBusinessUnit";
import { convertCodeToPhase, createOperatorName } from "../../utils/validation";
import Account from "../../interfaces/Account";
import { convertTo12HrTime } from "../../utils/DateUtil";
import businessUnit from "../../interfaces/BusinessUnit";
import { CREATE_BU_LOCATION_NAME_DEFAULT_VALUE } from "../../utils/constants";
import ICreateBusinessUnitBody from "../../interfaces/ICreateBusinessUnitBody";

export interface BusinessUnitsState {
  builtBusinessUnits: businessUnit[];
  locationTypes: string[];
  countryCodes: string[];
  selectedUnit: any;
  selectedUnitStatus: string;
  bulkUploadStatus: string;
  saveAllStatus: string;
  status: string;
  error: any;
  lastUpdated: string;
}

const initialState: BusinessUnitsState = {
  builtBusinessUnits: [],
  locationTypes: [],
  countryCodes: [],
  selectedUnit: null,
  selectedUnitStatus: IDLE,
  bulkUploadStatus: IDLE,
  saveAllStatus: IDLE,
  status: IDLE,
  error: null,
  lastUpdated: "",
};

export const fetchBusinessUnits = createAsyncThunk(
  "/business-units",
  async (param, thunkAPI) => {
    const businessUnitList: BusinessUnit[] = await getAllBusinessUnits();
    let locationTypes: string[] = [];
    let countryCodes: string[] = [];

    for (const element of businessUnitList) {
      let builtBusinessUnit: any = {
        ...element,
        operatorFullName: createOperatorName(element.operatorPreferredName),
      };
      builtBusinessUnit.isCompanyOwned =
        builtBusinessUnit.operatorFullName === "Company Owned";
      if (!locationTypes.includes(builtBusinessUnit.locationTypeCode)) {
        locationTypes.push(builtBusinessUnit.locationTypeCode);
      }
      if (!countryCodes.includes(builtBusinessUnit.isoCountryCode)) {
        countryCodes.push(builtBusinessUnit.isoCountryCode);
      }
    }
    return { businessUnitList, countryCodes, locationTypes };
  }
);
export const getArmoredCarBusinessUnitById = createAsyncThunk(
  "/business-units/id",
  async (payload: any, thunkAPI) => {
    const body = {
      id: payload.businessUnitUuid,
      metadata: payload.metadata,
      projectedGrandOpeningDate: payload.projectedGrandOpeningDate
        ? Date.parse(payload.projectedGrandOpeningDate)
        : payload.projectedGrandOpeningDate,
      phase: payload.phase,
      locationNumber: payload.locationNumber,
      businessUnitNumber: payload.businessUnitNumber,
      startDate: payload.creationDate
        ? Date.parse(payload.creationDate)
        : payload.creationDate,
      coOwned: payload.isCompanyOwned,
      coOwnedOperatorNumber: payload.coOwnedOperatorNumber,
      lastSalesDate: payload.lastSalesDate
        ? Date.parse(payload.lastSalesDate)
        : payload.lastSalesDate,
      operatorName: payload.operatorFullName,
      operatorNumber: payload.operatorNumber,
      locationName: payload.locationName,
      fein: payload.fein,
      entityName: payload.entityName,
      endDate: payload.endDate ? Date.parse(payload.endDate) : payload.endDate,
      businessUnitHistory: payload.businessUnitHistory,
      accountHistory: payload.accountHistory,
      active: payload.active,
      creationDate: payload.creationDate
        ? Date.parse(payload.creationDate)
        : payload.creationDate,
      firstSalesDate: payload.firstSalesDate
        ? Date.parse(payload.firstSalesDate)
        : payload.firstSalesDate,
      operatorTypeCode: payload.operatorTypeCode,
      openDate: payload.projectedGrandOpeningDate
        ? Date.parse(payload.projectedGrandOpeningDate)
        : payload.projectedGrandOpeningDate,
    };

    console.log(body);
    const businessUnit: businessUnit = await getBusinessUnitById(body);
    let tempBusinessUnitHistory: any[] = [];
    for (let i = 0; i < businessUnit.businessUnitHistory.length; i++) {
      const currentBUHistory = businessUnit.businessUnitHistory[i];
      tempBusinessUnitHistory.push({
        ...currentBUHistory,
        operatorFullName: createOperatorName(
          currentBUHistory.operatorPreferredName
        ),
      });
    }
    let tempAccountHistory: any = [];
    if (businessUnit.accountHistory) {
      for (let i = 0; i < businessUnit.accountHistory.length; i++) {
        let currentAccount = businessUnit.accountHistory[i];
        tempAccountHistory.push({
          ...currentAccount,
          touchToneOrderDeadline: convertTo12HrTime(
            currentAccount.touchToneOrderDeadline
          ),
          ceoOrderDeadline: convertTo12HrTime(currentAccount.ceoOrderDeadline),
        });
      }
    }
    let completeBusinessUnit = {
      ...businessUnit,
      phase: convertCodeToPhase(businessUnit.phase),
      businessUnitHistory: tempBusinessUnitHistory,
      accountHistory: tempAccountHistory,
      operatorFullName: createOperatorName(businessUnit.operatorPreferredName),
    };

    return completeBusinessUnit;
  }
);

export const updateBusinessUnit = createAsyncThunk(
  "/business-units/save",
  async (businessUnitPayload: any, thunkAPI) => {
    let businessUnitInfo: any = businessUnitPayload.tempForm;
    let test: boolean = businessUnitInfo.accountHistory == undefined;
    let isEditing = businessUnitPayload.isEditing;

    const operatorFirstName = businessUnitInfo.operatorFullName.split(" ")[0];
    const operatorLastName = businessUnitInfo.operatorFullName.split(" ")[1];
    let accountHistory = businessUnitInfo.accountHistory;

    const fields = {
      businessUnitId: "",
      businessUnitUuid: businessUnitInfo.businessUnitUuid,
      businessUnitNumber: businessUnitInfo.businessUnitNumber,
      businessUnitStartDate: businessUnitInfo.creationDate,
      phase: businessUnitInfo.phase,
      businessUnitEndDate: businessUnitInfo.endDate,
      accountNumber: businessUnitInfo.bankAccountNumber,
      bankName: businessUnitInfo.bankName,
      routingNumber: businessUnitInfo.bankRoutingNumber,
      cashProcessingVault: businessUnitInfo.cashProcessingVault,
      changeOrderingLeadDays: businessUnitInfo.changeLeadDays,

      ceoCompanyId: businessUnitInfo.ceoCompanyId,
      ceoLoginId: businessUnitInfo.ceoLoginId,
      ceoTempPassword: businessUnitInfo.ceoTempPassword,
      ceoOrderDeadline: businessUnitInfo.ceoOrderDeadline,

      monthlyFcrExpense: businessUnitInfo.monthlyFcrExpense,
      operationsStartDate: businessUnitInfo.operationsStartDate,
      // force '' to null, API only returns operationsEndDate with null for getAllBusinessUnits
      operationsEndDate:
        businessUnitInfo.operationsEndDate === ""
          ? null
          : businessUnitInfo.operationsEndDate,
      oracleBankChange: businessUnitInfo.oracleBankChange,
      safeSize: businessUnitInfo.safeSize,
      smartSafeSleeve: businessUnitInfo.smartSafeSleeve,
      hasSmartSafe: businessUnitInfo.hasSmartSafe,

      armoredCarService: businessUnitInfo.armoredCarService,
      armoredCarServicingDays:
        businessUnitInfo.armoredCarServicingDays.join("/"),
      monthlyArmoredCarFee: businessUnitInfo.monthlyArmoredCarFee,
      armoredCarTransportBranch: businessUnitInfo.armoredCarTransportBranch,

      touchToneGenericQuestions: businessUnitInfo.touchToneGenericQuestions,
      touchToneLogin: businessUnitInfo.touchToneLogin,
      touchToneOrderDeadline: businessUnitInfo.touchToneOrderDeadline,
      touchTonePassword: businessUnitInfo.touchTonePassword,
      touchTonePhoneNumber: businessUnitInfo.touchTonePhoneNumber,
    };

    if (isEditing && businessUnitInfo.accountHistory != undefined) {
      // The current account history object is returned as idx 0
      businessUnitInfo.accountHistory[0] = {
        ...fields,
        id: businessUnitInfo.id,
      };
    } else if (businessUnitInfo.accountHistory != undefined) {
      businessUnitInfo.accountHistory.push(fields);
    }

    const body = {
      businessUnitId: "",
      businessUnitUuid: businessUnitInfo.businessUnitUuid,
      phase: businessUnitInfo.phase,
      locationNumber: businessUnitInfo.locationNumber,
      businessUnitNumber: businessUnitInfo.businessUnitNumber,
      startDate: businessUnitInfo.startDate,
      operatorPreferredName: operatorFirstName,
      operatorFirstName: operatorFirstName,
      operatorLastName: operatorLastName,
      locationName: businessUnitInfo.locationName,
      fein: businessUnitInfo.fein,
      entityName: businessUnitInfo.entityName,

      businessUnitHistory: businessUnitInfo.businessUnitHistory,
      accountHistory: accountHistory,
    };

    await updateBusinessUnitInfo(body);
  }
);

export const updateBusinessUnitList = createAsyncThunk(
  "/business-units/save_all",
  async (builtBusinessUnitList: BuiltBusinessUnit[], thunkAPI) => {
    let businessUnitlist: any[] = new Array();
    for (const element of builtBusinessUnitList) {
      let accountId: number =
        element.activeAccountIndex >= 0
          ? // @ts-ignore
            element.accountHistory[element.activeAccountIndex].id
          : 0;
      let oracleBankChange: boolean =
        element.activeAccountIndex >= 0
          ? // @ts-ignore
            element.accountHistory[element.activeAccountIndex].oracleBankChange
          : false;
      let accountHistorylist: Account[] = new Array();
      // @ts-ignore
      const account: Account = {
        id: accountId,
        businessUnitId: element.businessUnitId,
        operationsStartDate: element.operationsStartDate,
        operationsEndDate: element.operationsEndDate
          ? element.operationsEndDate
          : "",
        armoredCarService: element.armoredCarService,
        armoredCarServicingDays: element.armoredCarServicingDays,
        bankName: element.bankName,
        accountNumber: element.accountNumber,
        routingNumber: element.routingNumber,
        changeOrderingLeadDays: element.changeOrderingLeadDays,
        ceoCompanyId: element.ceoCompanyId,
        ceoLoginId: element.ceoLoginId,
        ceoTempPassword: element.ceoTempPassword,
        ceoOrderDeadline: element.ceoOrderDeadline,
        touchTonePhoneNumber: element.touchTonePhoneNumber,
        touchToneGenericQuestions: element.touchToneGenericQuestions,
        touchToneLogin: element.touchToneLogin,
        touchTonePassword: element.touchTonePassword,
        touchToneOrderDeadline: element.touchToneOrderDeadline,
        armoredCarTransportBranch: element.armoredCarTransportBranch,
        cashProcessingVault: element.cashProcessingVault,
        safeSize: element.safeSize,
        smartSafeSleeve: element.smartSafeSleeve,
        monthlyArmoredCarFee: element.monthlyArmoredCarFee,
        monthlyFcrExpense: element.monthlyFcrExpense,
        oracleBankChange: oracleBankChange,
      };
      accountHistorylist.push(account);
      const businessUnit = {
        businessUnitId: element.businessUnitId,

        phase: element.phase,
        locationNumber: element.locationNumber,
        businessUnitNumber: element.businessUnitNumber,
        startDate: element.startDate,
        operatorPreferredName: element.operatorFirstName,
        operatorFirstName: element.operatorFirstName,
        operatorLastName: element.operatorLastName,
        locationName: element.locationName,
        fein: element.fein,
        entityName: element.entityName,
        businessUnitHistory: element.businessUnitHistory,
        accountHistory: accountHistorylist,
      };

      businessUnitlist.push(businessUnit);
    }
    await updateBusinessUnitListInfo(businessUnitlist);
  }
);

export const createBusinessUnit = createAsyncThunk(
  "/business-units/create",
  async (businessUnit: ICreateBusinessUnitBody, thunkAPI) => {
    const body: ICreateBusinessUnitBody = {
      businessUnitId: businessUnit.businessUnitId,
      businessUnitNumber: businessUnit.businessUnitNumber,
      locationNumber: businessUnit.locationNumber,
      startDate: businessUnit.startDate,
      phase: businessUnit.phase,
      /**
       *  These are defaulted in order for the sorting feature to not break.
       *  These values will be overwritten with the correct dates when the
       *  FCR load process runs.
       */
      openDate: businessUnit.startDate,
      locationName: CREATE_BU_LOCATION_NAME_DEFAULT_VALUE,
    };

    let response;

    try {
      response = await createNewBusinessUnit(body);
      //dispatch({ type: UPDATE_ARMORED_CAR_BUSINESS_UNITS_SUCCESS, payload: 'Business Unit Successfully Updated' });
    } catch (err) {
      //dispatch({ type: UPDATE_ARMORED_CAR_BUSINESS_UNITS_FAILURE, payload: 'Business Unit Update Unsuccessful: ' + err.data.message });
      return response;
    }
    return response;
  }
);

export const bulkUpload = createAsyncThunk(
  "/business-units/bulkUpload",
  async (businessUnit: any, thunkAPI) => {
    const body: any = businessUnit;

    let response;

    const importArr: any = [];
    importArr.push(businessUnit);

    response = await bulkImportUpload(body);
    return response;
  }
);

export const businessSlice = createSlice({
  name: "Business",
  initialState,
  reducers: {
    alertRefreshBusinessUnits: (state) => {
      state.status = NEEDS_REFRESH;
    },
    updateBusinessUnitInformation: (state, action) => {
      state.builtBusinessUnits = action.payload;
    },
    resetBulkUploadStatus: (state) => {
      state.bulkUploadStatus = IDLE;
    },
    resetSaveAllStatus: (state) => {
      state.saveAllStatus = IDLE;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchBusinessUnits.pending, (state, action) => {
        state.status = LOADING;
      })
      .addCase(fetchBusinessUnits.fulfilled, (state, action) => {
        state.status = SUCCEEDED;
        state.builtBusinessUnits = action.payload.businessUnitList;
        state.locationTypes = action.payload.locationTypes;
        state.countryCodes = action.payload.countryCodes;
      })
      .addCase(fetchBusinessUnits.rejected, (state, action) => {
        state.status = FAILED;
        state.error = action.error.message;
      })
      .addCase(getArmoredCarBusinessUnitById.pending, (state, action) => {
        state.selectedUnitStatus = LOADING;
      })
      .addCase(getArmoredCarBusinessUnitById.fulfilled, (state, action) => {
        state.selectedUnitStatus = SUCCEEDED;
        state.selectedUnit = action.payload;
      })
      .addCase(getArmoredCarBusinessUnitById.rejected, (state, action) => {
        state.selectedUnitStatus = FAILED;
        state.error = action.error.message;
      })
      .addCase(updateBusinessUnit.pending, (state, action) => {
        state.selectedUnitStatus = LOADING;
      })
      .addCase(updateBusinessUnit.fulfilled, (state, action) => {
        state.selectedUnitStatus = SUCCEEDED;
        state.selectedUnit = action.payload;
      })
      .addCase(updateBusinessUnit.rejected, (state, action) => {
        state.selectedUnitStatus = FAILED;
        state.error = action.error.message;
      })
      .addCase(updateBusinessUnitList.pending, (state, action) => {
        state.saveAllStatus = LOADING;
      })
      .addCase(updateBusinessUnitList.fulfilled, (state, action) => {
        state.saveAllStatus = SUCCEEDED;
      })
      .addCase(updateBusinessUnitList.rejected, (state, action) => {
        state.saveAllStatus = FAILED;
        state.error = action.error.message;
      })
      .addCase(createBusinessUnit.pending, (state, action) => {
        state.selectedUnitStatus = LOADING;
      })
      .addCase(createBusinessUnit.fulfilled, (state, action) => {
        state.selectedUnitStatus = SUCCEEDED;
        state.selectedUnit = action.payload;
      })
      .addCase(createBusinessUnit.rejected, (state, action) => {
        state.selectedUnitStatus = FAILED;
        state.error = action.error.message;
      })
      .addCase(bulkUpload.pending, (state, action) => {
        state.bulkUploadStatus = LOADING;
      })
      .addCase(bulkUpload.fulfilled, (state, action) => {
        state.bulkUploadStatus = SUCCEEDED;
        //state.selectedUnit = action.payload;
      })
      .addCase(bulkUpload.rejected, (state, action) => {
        state.bulkUploadStatus = FAILED;
        state.error = action.error.message;
      });
  },
});

export default businessSlice.reducer;
export const {
  alertRefreshBusinessUnits,
  resetBulkUploadStatus,
  resetSaveAllStatus,
} = businessSlice.actions;
export const businessSelector = (state: RootState) =>
  state.businessUnits.builtBusinessUnits;
export const countryCodesSelector = (state: RootState) =>
  state.businessUnits.countryCodes;
export const locationTypeSelector = (state: RootState) =>
  state.businessUnits.locationTypes;
export const businessUnitByIdSelector = (state: RootState) =>
  state.businessUnits.selectedUnit;
